/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { downgradeComponent, downgradeInjectable } from '@angular/upgrade/static';
import { Meta } from '@angular/platform-browser';
import { AutocompleteComponent } from '@ng-falcon/controls/autocomplete/autocomplete.component';
import { NgfRichTextViewerComponent } from '@ng-falcon/ngf-rich-text/ngf-rich-text-viewer/ngf-rich-text-viewer.component';
import { OptionsContainerComponent } from '@ng-falcon/controls/option/options-container/options-container.component';
import { OptionComponent } from '@ng-falcon/controls/option/option/option.component';
import { TesterNavigationHeaderComponent } from '@ng-falcon/features/tester-navigation-header/tester-navigation-header.component';
import { TesterNavigationSidebarComponent } from '@ng-falcon/features/tester-navigation-sidebar/tester-navigation-sidebar.component';
import { UnauthenticatedHomeComponent } from './home/unauthenticated-home/unauthenticated-home.component';
import { UtEditorComponent } from './common/ut-editor/ut-editor.component';
import { CommunityFeedItemLikeBtnComponent } from './common/community/likes/community-feed-item-like-btn/community-feed-item-like-btn.component';
import { CommunityFeedItemLikeCountComponent } from './common/community/likes/community-feed-item-like-count/community-feed-item-like-count.component';
import { TagBtnComponent } from './common/components/tag-btn/tag-btn.component';
import { CommentBtnComponent } from './shared/comment-btn/comment-btn.component';
import { IdentityVerificationBannerComponent } from './common/components/identity-verification-banner/identity-verification-banner.component';
import { PersonaVerificationBannerComponent } from './common/components/persona-verification-banner/persona-verification-banner.component';
import { CommunityFeedItemHideComponent } from './common/community/hide/community-feed-item-hide.component';
import { CommunityFeedItemFlagComponent } from './common/community/flag/community-feed-item-flag.component';
import { CommunityFeedItemSubscribeComponent } from './common/community/subscribe/community-feed-item-subscribe.component';
import { CommunityArchiveBtnComponent } from './common/community/archive/community-archive-btn.component';
import { CommunityFollowBtnComponent } from './common/community/follow/community-follow-btn.component';
import { SocialSharingComponent } from './common/social-sharing/social-sharing.component';
import { AvatarBadgesComponent } from './common/avatar-badges/avatar-badges.component';
import { DynamicTooltipComponent } from './common/dynamic-tooltip/dynamic-tooltip.component';
import { RecommendationsComponent } from './article/recommendations/recommendations.component';
import { ImpersonationOverlayComponent } from './common/components/impersonation-overlay/impersonation-overlay.component';
import { UtCustomDropdownComponent } from './common/components/custom-dropdown/ut-custom-dropdown.component';
import { FeedPaginatorComponent } from './common/components/feed-paginator/feed-paginator.component';
import { CommentSectionComponent } from './common/community/comment-section/comment-section.component';
import { CommentSectionShortComponent } from './common/community/comment-section-short/comment-section-short.component';
import { SingleArticleComponent } from './article/single-article/single-article.component';
import { ArticleFormComponent } from './article/article-form/article-form.component';
import { LoadMoreComponent } from './common/components/load-more/load-more.component';
import { LoadingDotsComponent } from './common/components/loading-dots/loading-dots.component';
import { ArticlesComponent } from './article/articles/articles.component';
import { PendingArticleComponent } from './article/pending-article/pending-article.component';
import { LeaderboardComponent } from './leaderboard/leaderboard.component';
import { HallOfFameService } from './services/hall-of-fame.service';
import { CommentEmojiService } from './services/comment-emojis.service';
import { FeedStoreService } from './services/feed-store.service';
import { KeysetFeedStoreService } from './services/keyset-feed-store.service';
import { StateStoreService } from './services/state-store.service';
import { QuizContainerComponent } from './academy/quiz/quiz-container/quiz-container.component';
import { QuizResultsContainerComponent } from './academy/quiz/quiz-results-container/quiz-results-container.component';
import { AcademyQuizService } from './academy/services/academy-quiz.service';
import { ArchiveService } from './services/archive.service';
import { ArticleService } from './services/article.service';
import { CommentService } from './services/comment.service';
import { FlagService } from './services/flag.service';
import { LikeService } from './services/like.service';
import { PageViewService } from './services/page-view.service';
import { ProjectService } from './services/project.service';
import { SubscribeService } from './services/subscribe.service';
import { AcademyCatalogComponent } from './academy/catalog/academy-catalog.component';
import { AcademyCertificationsComponent } from './academy/certifications/academy-certifications.component';
import { CertificationDetailComponent } from './academy/certifications/certification-detail/certification-detail.component';
import { CoursePageComponent } from './academy/course/course-page.component';
import { CommunityPageComponent } from './community-page/community-page.component';
import { ActivityFeedPageComponent } from './activity-feed/activity-feed-page.component';
import { DashboardComponent } from './home/dashboard/dashboard.component';
import { CommunityProfileComponent } from './community-profile/community-profile.component';
import { MyForumsPageComponent } from './my-forums-page/my-forums-page.component';
import { TopicFormPageComponent } from './topic-form-page/topic-form-page.component';
import { ForumsPageComponent } from './forums-page/forums-page.component';
import { TopicPageComponent } from './forums-page/topic-page.component';
import { ArticlesPageComponent } from './articles-page/articles-page.component';
import { ArticlePageComponent } from './articles-page/article-page.component';
import { ArticleFormPageComponent } from './articles-page/article-form-page.component';
import { SearchPageComponent } from './search-page/search-page.component';
import { ProjectsPageComponent } from './projects-page/projects-page.component';
import { ProjectListPageComponent } from './projects-page/tag-list/project-list-page.component';
import { ProjectPageComponent } from './projects-page/project/project-page.component';

declare const angular: any;

angular.module('uTestV2')
  .directive(
    'utUnauthenticatedHome',
    downgradeComponent({ component: UnauthenticatedHomeComponent }),
  )
  .directive(
    'utEditor',
    downgradeComponent({ component: UtEditorComponent }),
  )
  .directive(
    'ngfAutocomplete',
    downgradeComponent({ component: AutocompleteComponent }),
  )
  .directive(
    'ngfRichTextViewer',
    downgradeComponent({ component: NgfRichTextViewerComponent }),
  )
  .directive(
    'ngfOptionsContainer',
    downgradeComponent({ component: OptionsContainerComponent }),
  )
  .directive(
    'ngfOption',
    downgradeComponent({ component: OptionComponent }),
  )
  .directive(
    'utCommunityFeedItemLikeBtn',
    downgradeComponent({ component: CommunityFeedItemLikeBtnComponent }),
  )
  .directive(
    'utCommunityFeedItemLikeCount',
    downgradeComponent({ component: CommunityFeedItemLikeCountComponent }),
  )
  .directive(
    'utTagBtn',
    downgradeComponent({ component: TagBtnComponent }),
  )
  .directive(
    'utCommentBtn',
    downgradeComponent({ component: CommentBtnComponent }),
  )
  .directive(
    'appIdentityVerificationBanner',
    downgradeComponent({ component: IdentityVerificationBannerComponent }),
  )
  .directive(
    'utPersonaVerificationBanner',
    downgradeComponent({ component: PersonaVerificationBannerComponent }),
  )
  .directive(
    'utCommunityFeedItemHide',
    downgradeComponent({ component: CommunityFeedItemHideComponent }),
  )
  .directive(
    'utCommunityFeedItemFlag',
    downgradeComponent({ component: CommunityFeedItemFlagComponent }),
  )
  .directive(
    'utCommunityArchiveBtn',
    downgradeComponent({ component: CommunityArchiveBtnComponent }),
  )
  .directive(
    'utCommunityFollowBtn',
    downgradeComponent({ component: CommunityFollowBtnComponent }),
  )
  .directive(
    'utCommunityFeedItemSubscribe',
    downgradeComponent({ component: CommunityFeedItemSubscribeComponent }),
  )
  .directive(
    'utSocialSharing',
    downgradeComponent({ component: SocialSharingComponent }),
  )
  .directive(
    'utAvatarBadges',
    downgradeComponent({ component: AvatarBadgesComponent }),
  )
  .directive(
    'utDynamicTooltip',
    downgradeComponent({ component: DynamicTooltipComponent }),
  )
  .directive(
    'utRecommendations',
    downgradeComponent({ component: RecommendationsComponent }),
  )
  .directive(
    'utImpersonationOverlay',
    downgradeComponent({ component: ImpersonationOverlayComponent }),
  )
  .directive(
    'utCustomDropdown',
    downgradeComponent({ component: UtCustomDropdownComponent }),
  )
  .directive(
    'utFeedPaginator',
    downgradeComponent({ component: FeedPaginatorComponent }),
  )
  .directive(
    'utCommentSection',
    downgradeComponent({ component: CommentSectionComponent }),
  )
  .directive(
    'utCommentSectionShort',
    downgradeComponent({ component: CommentSectionShortComponent }),
  )
  .directive(
    'utSingleArticle',
    downgradeComponent({ component: SingleArticleComponent }),
  )
  .directive(
    'utArticleForm',
    downgradeComponent({ component: ArticleFormComponent }),
  )
  .directive(
    'utLoadMore',
    downgradeComponent({ component: LoadMoreComponent }),
  )
  .directive(
    'utLoadingDots',
    downgradeComponent({ component: LoadingDotsComponent }),
  )
  .directive(
    'utArticles',
    downgradeComponent({ component: ArticlesComponent }),
  )
  .directive(
    'utPendingArticle',
    downgradeComponent({ component: PendingArticleComponent }),
  )
  .directive(
    'utLeaderboard',
    downgradeComponent({ component: LeaderboardComponent }),
  )
  .directive(
    'utAcademyCatalog',
    downgradeComponent({ component: AcademyCatalogComponent }),
  )
  .directive(
    'utAcademyCertifications',
    downgradeComponent({ component: AcademyCertificationsComponent }),
  )
  .directive(
    'utCommunityPage',
    downgradeComponent({ component: CommunityPageComponent }),
  )
  .directive(
    'utCertificationDetail',
    downgradeComponent({ component: CertificationDetailComponent }),
  )
  .directive(
    'utCoursePage',
    downgradeComponent({ component: CoursePageComponent }),
  )
  .factory(
    'Meta',
    downgradeInjectable(Meta as any),
  )
  .factory(
    'HallOfFameService',
    downgradeInjectable(HallOfFameService as any),
  )
  .factory(
    'AcademyQuizService',
    downgradeInjectable(AcademyQuizService as any),
  )
  .factory(
    'ArchiveService',
    downgradeInjectable(ArchiveService as any),
  )
  .factory(
    'FlagService',
    downgradeInjectable(FlagService as any),
  )
  .factory(
    'LikeService',
    downgradeInjectable(LikeService as any),
  )
  .factory(
    'PageViewService',
    downgradeInjectable(PageViewService as any),
  )
  .factory(
    'ArticleService',
    downgradeInjectable(ArticleService as any),
  )
  .factory(
    'ProjectService',
    downgradeInjectable(ProjectService as any),
  )
  .factory(
    'SubscribeService',
    downgradeInjectable(SubscribeService as any),
  )
  .factory('commentEmojis', downgradeInjectable(CommentEmojiService as any))
  .factory('FeedStore', downgradeInjectable(FeedStoreService as any))
  .factory('KeysetFeedStore', downgradeInjectable(KeysetFeedStoreService as any))
  .factory('stateStore', downgradeInjectable(StateStoreService as any))
  .directive(
    'utAcademyQuizContainer',
    downgradeComponent({ component: QuizContainerComponent }),
  )
  .directive(
    'utAcademyQuizResultsContainer',
    downgradeComponent({ component: QuizResultsContainerComponent }),
  )
  .directive(
    'ngfTesterNavigationHeader',
    downgradeComponent({ component: TesterNavigationHeaderComponent }),
  )
  .directive(
    'ngfTesterNavigationSidebar',
    downgradeComponent({ component: TesterNavigationSidebarComponent }),
  )
  .directive(
    'utActivityFeedPage',
    downgradeComponent({ component: ActivityFeedPageComponent }),
  )
  .directive(
    'utDashboard',
    downgradeComponent({ component: DashboardComponent }),
  )
  .directive(
    'utCommunityProfile',
    downgradeComponent({ component: CommunityProfileComponent }),
  )
  .directive(
    'utMyForumsPage',
    downgradeComponent({ component: MyForumsPageComponent }),
  )
  .directive(
    'utTopicFormPage',
    downgradeComponent({ component: TopicFormPageComponent }),
  )
  .directive(
    'utForumsPage',
    downgradeComponent({ component: ForumsPageComponent }),
  )
  .directive(
    'utTopicPage',
    downgradeComponent({ component: TopicPageComponent }),
  )
  .directive(
    'utArticlesPage',
    downgradeComponent({ component: ArticlesPageComponent }),
  )
  .directive(
    'utArticlePage',
    downgradeComponent({ component: ArticlePageComponent }),
  )
  .directive(
    'utArticleFormPage',
    downgradeComponent({ component: ArticleFormPageComponent }),
  )
  .directive(
    'utSearchPage',
    downgradeComponent({ component: SearchPageComponent }),
  )
  .directive(
    'utProjectsPage',
    downgradeComponent({ component: ProjectsPageComponent }),
  )
  .directive(
    'utProjectListPage',
    downgradeComponent({ component: ProjectListPageComponent }),
  )
  .directive(
    'utProjectPage',
    downgradeComponent({ component: ProjectPageComponent }),
  )
  .factory(
    'CommentService',
    downgradeInjectable(CommentService as any),
  );
